import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch, fmtDate, fmtTime } from "../api.js";
import {
  getMasterVisibleAppointmentStatusMeta,
  type AdminAppointmentStatus,
} from "../appointmentsView.js";
import {
  formatClientAppointmentSummary,
  formatClientNotePreview,
  type ClientAppointmentSummary,
} from "../clientListView.js";

interface Client {
  id: string;
  name: string;
  phone: string | null;
  telegramId: string | null;
  maxId: string | null;
  vkId: string | null;
  notificationChannel: "TELEGRAM" | "MAX" | "VK" | null;
  email?: string | null;
  notes: string | null;
  createdAt: string;
  appointmentSummary: ClientAppointmentSummary | null;
}

interface ClientAppointment {
  id: string;
  startsAt: string;
  status: AdminAppointmentStatus;
  actualPrice: number | null;
  service: { name: string; priceFrom: number };
}

const CHANNEL_ICON: Record<NonNullable<Client["notificationChannel"]>, string> = {
  TELEGRAM: "TG", MAX: "MAX", VK: "VK",
};

function pluralClients(n: number): string {
  if (n % 10 === 1 && n % 100 !== 11) return "клиент";
  if ([2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100)) return "клиента";
  return "клиентов";
}

function ClientHistory({ token, clientId }: { token: string; clientId: string }) {
  const { data = [], isLoading } = useQuery({
    queryKey: ["client-appointments", clientId],
    queryFn: () => apiFetch<ClientAppointment[]>(token, `/api/clients/${clientId}/appointments`),
  });

  if (isLoading) return <p className="text-xs text-gray-400 py-2">Загрузка истории...</p>;
  if (!data.length) return <p className="text-xs text-gray-400 py-2">Записей нет</p>;

  const completed = data.filter((a) => a.status === "COMPLETED");
  const totalRevenue = completed.reduce((sum, a) => sum + (a.actualPrice ?? a.service.priceFrom), 0);
  const lastVisit = data.find((a) => a.status === "COMPLETED" || a.status === "NO_SHOW");

  return (
    <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
      {/* Client summary */}
      {(completed.length > 0 || data.length > 0) && (
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-gray-50 rounded-xl p-2 text-center">
            <p className="text-base font-bold text-gray-800">{data.length}</p>
            <p className="text-[10px] text-gray-400">визитов</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-2 text-center">
            <p className="text-base font-bold text-blue-600">{completed.length}</p>
            <p className="text-[10px] text-gray-400">завершено</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-2 text-center">
            <p className="text-base font-bold text-green-600">
              {totalRevenue > 0 ? Math.round(totalRevenue / 100).toLocaleString("ru-RU") + " ₽" : "—"}
            </p>
            <p className="text-[10px] text-gray-400">потрачено</p>
          </div>
        </div>
      )}
      {lastVisit && (
        <p className="text-xs text-gray-400">
          Последний визит: <span className="text-gray-600 font-medium">{fmtDate(lastVisit.startsAt)}</span>
        </p>
      )}
      {/* History list */}
      <div>
        <p className="text-xs font-semibold text-gray-500 mb-2">История</p>
        <div className="space-y-1.5">
          {data.map((a) => {
            const statusMeta = getMasterVisibleAppointmentStatusMeta(a.status);

            return (
              <div key={a.id} className="flex items-center justify-between text-xs bg-gray-50 rounded-lg px-2.5 py-1.5">
                <span className="text-gray-600 truncate">
                  {fmtDate(a.startsAt)} {fmtTime(a.startsAt)} · {a.service.name}
                </span>
                {statusMeta && (
                  <span className={`font-medium ml-2 shrink-0 ${statusMeta.textClass}`}>
                    {statusMeta.label}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function ClientsPage({ token }: { token: string }) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string | null>(null);

  const { data: clients = [], isLoading } = useQuery({
    queryKey: ["clients", search],
    queryFn: () =>
      apiFetch<Client[]>(token, `/api/clients${search ? `?search=${encodeURIComponent(search)}` : ""}`),
    staleTime: 10_000,
  });

  return (
    <div className="mx-auto w-full max-w-2xl min-w-0 p-4">
      {/* Header */}
      <div className="mb-3 flex min-w-0 items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-gray-800">Клиенты</h2>
        {clients.length > 0 && (
          <span className="text-xs text-gray-400 font-medium">
            {clients.length} {pluralClients(clients.length)}
          </span>
        )}
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
        <input
          id="clients-search"
          name="clients-search"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Имя или телефон..."
          className="w-full border border-gray-200 rounded-xl pl-8 pr-4 py-2.5 text-sm bg-white placeholder-gray-400 focus:outline-none focus:border-primary/50"
        />
        {search && (
          <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none">×</button>
        )}
      </div>

      {isLoading && <div className="text-center py-10 text-gray-400">Загрузка...</div>}

      {!isLoading && clients.length === 0 && (
        <div className="text-center py-14">
          <p className="text-4xl mb-3">👥</p>
          <p className="text-gray-500 font-medium">{search ? "Клиентов не найдено" : "Клиентов ещё нет"}</p>
          {!search && <p className="text-sm text-gray-400 mt-1">Клиенты появятся после первой записи</p>}
        </div>
      )}

      <div className="space-y-2">
        {clients.map((c) => {
          const isOpen = selected === c.id;
          const appointmentSummaryText = formatClientAppointmentSummary(c.appointmentSummary);
          const notePreview = formatClientNotePreview(c.notes);

          return (
            <div key={c.id} className="min-w-0 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
              <button
                onClick={() => setSelected(isOpen ? null : c.id)}
                className="w-full p-4 text-left hover:bg-gray-50/50 transition"
              >
                <div className="flex min-w-0 items-center gap-3">
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-base shrink-0">
                    {c.name.charAt(0).toUpperCase()}
                  </div>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-800 truncate">{c.name}</p>
                    {c.phone ? (
                      <a
                        href={`tel:${c.phone}`}
                        className="text-sm text-primary hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {c.phone}
                      </a>
                    ) : (
                      <p className="text-sm text-gray-400">Телефон не указан</p>
                    )}
                    {appointmentSummaryText && (
                      <p className="mt-1 truncate text-xs font-medium text-gray-500">
                        {appointmentSummaryText}
                      </p>
                    )}
                    {notePreview && (
                      <p className="mt-1 truncate rounded-lg bg-amber-50 px-2 py-1 text-xs text-amber-700">
                        Заметка: {notePreview}
                      </p>
                    )}
                  </div>
                  {/* Badges */}
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    {c.notificationChannel && (
                      <span className="text-[10px] bg-gray-100 text-gray-500 rounded-full px-2 py-0.5 font-medium">
                        {CHANNEL_ICON[c.notificationChannel]}
                      </span>
                    )}
                    {!appointmentSummaryText && (
                      <p className="text-[10px] text-gray-400">
                        {new Date(c.createdAt).toLocaleDateString("ru-RU", { day: "numeric", month: "short" })}
                      </p>
                    )}
                    <span className="text-gray-300 text-xs">{isOpen ? "▲" : "▼"}</span>
                  </div>
                </div>
              </button>

              {/* Expanded */}
              {isOpen && (
                <div className="px-4 pb-4 border-t border-gray-50">
                  {(c.email ?? c.notes) && (
                    <div className="space-y-1 pt-3 pb-2">
                      {c.email && (
                        <p className="text-xs text-gray-500">
                          <span className="text-gray-400">Email: </span>
                          <a href={`mailto:${c.email}`} className="text-primary hover:underline">{c.email}</a>
                        </p>
                      )}
                      {c.notes && (
                        <p className="text-xs text-gray-500 bg-yellow-50 rounded-lg px-2 py-1.5 border border-yellow-100">
                          💬 {c.notes}
                        </p>
                      )}
                    </div>
                  )}
                  <ClientHistory token={token} clientId={c.id} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
